import { Truck } from "lucide-react";
import { cn } from "@/lib/utils";

// Phase 4j portal-polish — small equipment thumbnail. Customers
// identify rentals by what's on the jobsite, not by order number;
// dashboard cards + order detail hero render this at the left of
// each row. Three sizes: sm (48px) for compact rows, md (64px) for
// dashboard cards, lg (96px) for the order detail hero.
//
// `alt=""` is intentional — the equipment name lives in adjacent
// text so the image is decorative. A `name` is still required so
// callers can't accidentally render this without naming the item
// somewhere nearby.
type Size = "sm" | "md" | "lg";

const SIZE_CLASS: Record<Size, string> = {
  sm: "h-12 w-12",
  md: "h-16 w-16",
  lg: "h-24 w-24"
};

export function EquipmentThumb({
  src,
  name,
  size = "md",
  className
}: {
  src: string | null;
  name: string;
  size?: Size;
  className?: string;
}): JSX.Element {
  const dimensions = SIZE_CLASS[size];

  if (src) {
    return (
      <img
        src={src}
        alt=""
        data-testid="equipment-thumb-img"
        data-equipment-name={name}
        className={cn(
          dimensions,
          "flex-none rounded-md border border-zinc-200 object-cover",
          className
        )}
      />
    );
  }

  return (
    <div
      data-testid="equipment-thumb-placeholder"
      data-equipment-name={name}
      role="img"
      aria-label={`No photo for ${name}`}
      className={cn(
        dimensions,
        "flex-none rounded-md border border-zinc-200 bg-zinc-50",
        "grid place-items-center text-zinc-400",
        className
      )}
    >
      <Truck aria-hidden="true" className="h-5 w-5" />
    </div>
  );
}
